import { useForm, useFormContext, useWatch } from "react-hook-form";
import { memo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { placeOrderThunk } from "./redux/actions/checkoutPageActions";
import { type CheckoutForm, type CheckoutFormOptime } from "./components/CheckoutPage/CheckoutCart";
import { selectCartContainsDigitalServiceTenant } from "./redux/selectors/productEntrySelectors";
import {
  selectInitialDeliveryMode,
  selectInitialFreightPayment,
  selectSelectedUnloadingPoint,
} from "./redux/selectors/checkoutPageSelectors";
import { CURRENT_LANGUAGE } from "../utils/constants";
import { OPTIME_LANGUAGES } from "./components/CheckoutPage/CheckoutSummary/CheckoutOptimeSubscriptionForm";
import { DeliveryOptionItem } from "./components/CheckoutPage/DeliveryOption";
import { type ErrorCode } from "~/Cart/api/placeOrder";

const EMPTY_OPTIME_FORM: CheckoutFormOptime = {
  consent: false,
  country: "",
  email: "",
  language: "",
  lastName: "",
  name: "",
  plant: "",
  timeZone: "",
};

const EMPTY_FORM: CheckoutForm = {
  poNumber: "",
  deliveryCode: "",
  orderComment: "",
  completeDelivery: DeliveryOptionItem.PARTIAL,
  optime: EMPTY_OPTIME_FORM,
  paymentType: "account",
  freightPaymentCode: "",
  freightPaymentAccountNumber: "",
  creditCardID: "",
  orderType: "",
};

export function useCheckoutLogic() {
  const [placeOrderError, setPlaceOrderError] = useState<ErrorCode | undefined>();
  const formProps = useForm<CheckoutForm>({
    defaultValues: { ...EMPTY_FORM },
  });

  const {
    trigger,
    handleSubmit,
    getValues,
    formState: { errors },
  } = formProps;
  const dispatch = useDispatch();

  const unloadingPoint = useSelector(selectSelectedUnloadingPoint);
  const handleSubmitClick = useCallback(
    async (isCustomerInitiatedQuote: boolean) => {
      await handleSubmit(
        async (data) => {
          clearSavedForm();
          const placeOrderResult = await (dispatch(
            placeOrderThunk(data, isCustomerInitiatedQuote, unloadingPoint)
          ) as unknown as Promise<ErrorCode | undefined>);
          setPlaceOrderError(placeOrderResult);
        },
        (errors) => {
          const ref = (Object.values(errors)[0] as any)?.ref;
          if (ref) {
            ref.scrollIntoView();
            // -50 to roughly see the label above
            window.scrollBy(0, -50);
          }
        }
      )();
    },
    [unloadingPoint, errors, trigger, getValues, handleSubmit]
  );

  const containsDST = useSelector(selectCartContainsDigitalServiceTenant);
  const initialDeliveryCode = useSelector(selectInitialDeliveryMode);
  const initialFreightPayment = useSelector(selectInitialFreightPayment);

  useEffect(() => {
    let form: CheckoutForm | null = loadSavedForm();
    if (!form) {
      form = {
        ...EMPTY_FORM,
        deliveryCode: initialDeliveryCode,
        freightPaymentCode: initialFreightPayment,
      };

      if (containsDST) {
        form = {
          ...form,
          optime: {
            ...(form.optime || EMPTY_OPTIME_FORM),
            timeZone: resolveInitialTimezone(),
            language: resolveInitialLanguage(),
          },
        };
      }
    }
    formProps.reset(form);
  }, []);

  return {
    handleSubmitClick,
    formProps,
    showOptimeForm: containsDST,
    placeOrderError,
    resetPlaceOrderError: () => setPlaceOrderError(undefined),
  };
}

export const CheckoutFormSaver = memo(() => {
  const { control, formState } = useFormContext();
  const data = useWatch({ control }) as CheckoutForm;

  useEffect(() => {
    if (formState.isDirty && !formState.isSubmitSuccessful && !formState.isSubmitting) {
      saveForm(data);
    }
  });
  return null;
});

const STORAGE_KEY = "checkout.form";

const loadSavedForm = (): CheckoutForm | null => {
  const data = window.sessionStorage?.getItem(STORAGE_KEY);
  if (data) {
    return JSON.parse(data) as CheckoutForm;
  }

  return null;
};

export const clearSavedForm = () => {
  window.sessionStorage?.removeItem(STORAGE_KEY);
};

const saveForm = (form: CheckoutForm) => {
  window.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(form));
};

const resolveInitialTimezone = () => {
  let initialTimezone = "";
  try {
    initialTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (err) {}
  return initialTimezone;
};

const resolveInitialLanguage = () => {
  return OPTIME_LANGUAGES.includes(CURRENT_LANGUAGE) ? CURRENT_LANGUAGE : "";
};
